/* eslint-disable no-console */

/**
 * ComponentsLogger provides static methods for styled console logging.
 * Every log is prefixed with a branded [PRIMER] tag using advanced CSS styling.
 *
 * This is deliberately global, dependency-free state: `enabled` is a
 * page-level debug switch. Anything session-bound (Datadog, analytics)
 * lives on `Telemetry` instead.
 */
export class PrimerJSLogger {
  // Global flag to enable/disable console logging per level
  public static enabled: ('log' | 'info' | 'warn' | 'error' | 'debug')[] = [
    'warn',
    'error',
  ];

  // Default font style used across all log messages.
  private static readonly defaultFont: string =
    'font-family: Consolas, monospace; font-size: 12px;';

  // Branded tag and its style.
  private static readonly brandTag: string = '[PRIMER]';
  private static readonly brandStyle: string = `background: #24292e; color: #ffffff; padding: 2px 6px; border-radius: 3px; font-weight: bold; margin-right: 4px; ${PrimerJSLogger.defaultFont}`;

  // Styles for each log level.
  private static readonly styles = {
    log: `color: #4CAF50; ${PrimerJSLogger.defaultFont}`,
    info: `color: #2196F3; ${PrimerJSLogger.defaultFont}`,
    warn: `color: #FF9800; ${PrimerJSLogger.defaultFont}`,
    error: `color: #F44336; ${PrimerJSLogger.defaultFont}`,
    debug: `color: #9C27B0; ${PrimerJSLogger.defaultFont}`,
  };

  private static _log(
    level: 'log' | 'info' | 'warn' | 'error' | 'debug',
    message: string,
    ...args: unknown[]
  ): void {
    if (!PrimerJSLogger.enabled.includes(level)) {
      return;
    }
    console[level](
      `%c${PrimerJSLogger.brandTag}%c ${message}`,
      PrimerJSLogger.brandStyle,
      PrimerJSLogger.styles[level],
      ...args,
    );

    // Log Error objects separately to preserve their expandable properties
    args.forEach((arg) => {
      if (arg instanceof Error) {
        // If the error has a toJSON method, log its output for better visibility
        const errorWithToJson = arg as Error & { toJSON?: () => unknown };
        if (typeof errorWithToJson.toJSON === 'function') {
          console[level]('Error details:', errorWithToJson.toJSON());
        }
      }
    });
  }

  /**
   * Logs a standard message with Primer branding.
   * @param message The message to log.
   * @param args Additional data to log.
   */
  public static log(message: string, ...args: any[]): void {
    PrimerJSLogger._log('log', message, ...args);
  }

  /**
   * Logs an informational message with Primer branding.
   * @param message The message to log.
   * @param args Additional data to log.
   */
  public static info(message: string, ...args: any[]): void {
    PrimerJSLogger._log('info', message, ...args);
  }

  /**
   * Logs a warning message with Primer branding.
   * @param message The message to log.
   * @param args Additional data to log.
   */
  public static warn(message: string, ...args: any[]): void {
    PrimerJSLogger._log('warn', message, ...args);
  }

  /**
   * Logs an error message with Primer branding.
   * Does NOT send to Datadog - use `Telemetry.reportSdkError()` for
   * unexpected technical errors.
   * @param message The message to log.
   * @param args Additional data to log.
   */
  public static error(message: string, ...args: any[]): void {
    PrimerJSLogger._log('error', message, ...args);
  }

  /**
   * Logs a debug message with Primer branding.
   * @param message The message to log.
   * @param args Additional data to log.
   */
  public static debug(message: string, ...args: any[]): void {
    this._log('debug', message, ...args);
  }
}
